import { generateTypes } from '@evmos/eip712';
import { createMsgGrant } from '@evmos/proto';
import { GenericAuthorization as GenericAuthorizationProto } from '@evmos/proto/dist/proto/cosmos/authz/authz';
import { MessageGenerated } from '@evmos/proto/dist/messages/common';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { GenericAuthorization } from 'cosmjs-types/cosmos/authz/v1beta1/authz';
import { Any } from 'cosmjs-types/google/protobuf/any';
import { Timestamp } from 'cosmjs-types/google/protobuf/timestamp';
import {
  createFulfillOrderAuthorization,
  FULFILL_ORDER_AUTHORIZATION_TYPES,
  protoFulfillOrderAuthorization,
} from '../../eibc/eip712/fulfill-order-grant';

const GENERIC_AUTHORIZATION_TYPES = {
  TypeGrantAuthorizationValue: [ { name: 'msg', type: 'string' } ],
};

export interface MsgGrantProps {
  granter: string;
  grantee: string;
  authorization: Any;
  expiration?: Timestamp;
}

export const MSG_GRANT_TYPES = (params: MsgGrantProps) => ({
  MsgValue: [
    { name: 'granter', type: 'string' },
    { name: 'grantee', type: 'string' },
    { name: 'grant', type: 'TypeGrant' },
  ],
  TypeGrant: [
    { name: 'authorization', type: 'TypeGrantAuthorization' },
    { name: 'expiration', type: 'string' },
  ],
  TypeGrantAuthorization: [
    { name: 'type', type: 'string' },
    { name: 'value', type: 'TypeGrantAuthorizationValue' },
  ],
  ...getAuthorizationEIP712Types(params),
});

export const createTxMsgGrant = (
  context: TxContext,
  params: MsgGrantProps | MsgGrantProps[],
): TxPayload => {
  const typedData = createEIP712MsgGrant(context, params);
  const cosmosMsg = createCosmosMsgGrant(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgGrant = (
  context: TxContext,
  params: MsgGrantProps | MsgGrantProps[],
): EIP712TypedData => {
  const paramList = Array.isArray(params) ? params : [ params ];
  const types = generateTypes(MSG_GRANT_TYPES(paramList[0]));
  const messages = paramList.map((params) => ({
    type: 'cosmos-sdk/MsgGrant',
    value: {
      grant: { authorization: createEIP712Authorization(params), expiration: getEIP712dExpiration(params) },
      grantee: params.grantee,
      granter: context.sender.accountAddress,
    },
  }));
  return { types, message: messages.length === 1 ? messages[0] : messages };
};

const createCosmosMsgGrant = (
  context: TxContext,
  params: MsgGrantProps | MsgGrantProps[],
) => {
  return (Array.isArray(params) ? params : [ params ]).map((params) => {
    const grantMessage = createCosmosAuthorization(params);
    return createMsgGrant(
      context.sender.accountAddress,
      params.grantee,
      grantMessage,
      params.expiration?.seconds.toNumber() || 0,
    );
  });
};

const getAuthorizationEIP712Types = (params: MsgGrantProps): object => {
  if (params.authorization.typeUrl === '/dymensionxyz.dymension.eibc.FulfillOrderAuthorization') {
    return FULFILL_ORDER_AUTHORIZATION_TYPES(params.authorization.value);
  }
  return GENERIC_AUTHORIZATION_TYPES;
};

const createEIP712Authorization = (params: MsgGrantProps): object => {
  const { typeUrl, value } = params.authorization;
  if (typeUrl === '/dymensionxyz.dymension.eibc.FulfillOrderAuthorization') {
    return createFulfillOrderAuthorization(value);
  }
  return createGenericAuthorization(value);
};

const createCosmosAuthorization = (params: MsgGrantProps): MessageGenerated => {
  const { typeUrl, value } = params.authorization;
  if (typeUrl === '/dymensionxyz.dymension.eibc.FulfillOrderAuthorization') {
    return protoFulfillOrderAuthorization(value);
  }
  return protoCreateGenericAuthorization(value);
};

const createGenericAuthorization = (value: Uint8Array): object => {
  const authorization = GenericAuthorization.decode(value);
  return { type: 'cosmos-sdk/GenericAuthorization', value: authorization };
};

export const protoCreateGenericAuthorization = (value: Uint8Array) => ({
  message: GenericAuthorizationProto.fromBinary(value),
  path: GenericAuthorizationProto.typeName,
});

const getEIP712dExpiration = (params: MsgGrantProps): string => {
  const date = new Date();
  if (params.expiration) {
    date.setTime(params.expiration?.seconds.toNumber() * 1000);
  }
  return date.toISOString().replace('.000Z', 'Z');
};




